import React, { useEffect, useState } from 'react'
import {
    Box,
    Typography,
    Button,
    Container,
    keyframes,
    styled,
} from '@mui/material'
import { Home, Waves } from '@mui/icons-material'

// Keyframe animations
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(5deg); }
`

const wave = keyframes`
  0%, 100% { transform: translateX(0px); }
  50% { transform: translateX(-20px); }
`

const bubble = keyframes`
  0% { transform: translateY(100vh) scale(0); opacity: 0; }
  10% { opacity: 1; }
  90% { opacity: 1; }
  100% { transform: translateY(-100px) scale(1); opacity: 0; }
`

const swim = keyframes`
  0% { transform: translateX(-100px) scaleX(1); }
  50% { transform: translateX(50vw) scaleX(1); }
  51% { transform: translateX(50vw) scaleX(-1); }
  100% { transform: translateX(-100px) scaleX(-1); }
`

const glow = keyframes`
  0%, 100% { text-shadow: 0 0 20px rgba(140, 198, 63, 0.5); }
  50% { text-shadow: 0 0 30px rgba(140, 198, 63, 0.8), 0 0 40px rgba(140, 198, 63, 0.6); }
`

// Styled components
const OceanContainer = styled(Box)({
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #8cc63f 0%, #4a7c59 50%, #222 100%)',
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
})

const WaveLayer = styled(Box)<{ delay?: number }>(({ delay = 0 }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '200%',
    height: '100%',
    background: `linear-gradient(45deg, 
    rgba(140, 198, 63, 0.3) 0%, 
    rgba(34, 34, 34, 0.1) 50%, 
    rgba(140, 198, 63, 0.2) 100%)`,
    animation: `${wave} 6s ease-in-out infinite`,
    animationDelay: `${delay}s`,
    borderRadius: '50%',
    transform: 'translateY(50%)',
}))

const FloatingElement = styled(Box)<{ delay?: number; duration?: number }>(
    ({ delay = 0, duration = 3 }) => ({
        position: 'absolute',
        animation: `${float} ${duration}s ease-in-out infinite`,
        animationDelay: `${delay}s`,
        fontSize: '2rem',
        color: 'rgba(255, 255, 255, 0.7)',
        userSelect: 'none',
    })
)

const SwimmingFish = styled(Box)<{ delay?: number }>(({ delay = 0 }) => ({
    position: 'absolute',
    fontSize: '3rem',
    animation: `${swim} 15s linear infinite`,
    animationDelay: `${delay}s`,
    top: '20%',
    opacity: 0.6,
}))

const Bubble = styled(Box)<{ delay?: number; left?: number }>(
    ({ delay = 0, left = 50 }) => ({
        position: 'absolute',
        left: `${left}%`,
        width: '10px',
        height: '10px',
        backgroundColor: 'rgba(255, 255, 255, 0.3)',
        borderRadius: '50%',
        animation: `${bubble} 8s linear infinite`,
        animationDelay: `${delay}s`,
    })
)

const GlowText = styled(Typography)({
    animation: `${glow} 2s ease-in-out infinite`,
    color: '#fff',
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: '2rem',
})

const ContentBox = styled(Box)({
    position: 'relative',
    zIndex: 10,
    textAlign: 'center',
    padding: '3rem',
    background: 'rgba(0, 0, 0, 0.3)',
    backdropFilter: 'blur(10px)',
    borderRadius: '20px',
    border: '1px solid rgba(140, 198, 63, 0.3)',
    maxWidth: '600px',
})

const StyledButton = styled(Button)({
    background: 'linear-gradient(45deg, #8cc63f, #4a7c59)',
    color: 'white',
    padding: '12px 30px',
    fontSize: '1.1rem',
    borderRadius: '25px',
    textTransform: 'none',
    transition: 'all 0.3s ease',
    '&:hover': {
        background: 'linear-gradient(45deg, #4a7c59, #8cc63f)',
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 25px rgba(140, 198, 63, 0.4)',
    },
})

export const ErrorPage: React.FC = () => {
    const [bubbles, setBubbles] = useState<
        Array<{ id: number; delay: number; left: number }>
    >([])

    useEffect(() => {
        // Generate random bubbles
        const bubbleArray = Array.from({ length: 15 }, (_, i) => ({
            id: i,
            delay: Math.random() * 8,
            left: Math.random() * 100,
        }))
        setBubbles(bubbleArray)
    }, [])

    const marineElements = [
        { emoji: '🐠', top: '15%', left: '10%', delay: 0, duration: 4 },
        { emoji: '🐙', top: '60%', left: '85%', delay: 1, duration: 5 },
        { emoji: '🦑', top: '25%', left: '75%', delay: 2, duration: 3.5 },
        { emoji: '🐟', top: '70%', left: '15%', delay: 1.5, duration: 4.5 },
        { emoji: '🦈', top: '40%', left: '80%', delay: 3, duration: 6 },
        { emoji: '🐚', top: '80%', left: '25%', delay: 2.5, duration: 3 },
        { emoji: '⭐', top: '85%', left: '70%', delay: 0.5, duration: 4 },
        { emoji: '🌊', top: '10%', left: '90%', delay: 1.8, duration: 5 },
    ]

    const handleGoHome = () => {
        window.location.href = '/'
    }

    return (
        <OceanContainer>
            {/* Animated wave layers */}
            <WaveLayer delay={0} />
            <WaveLayer delay={2} />
            <WaveLayer delay={4} />

            {/* Swimming fish */}
            <SwimmingFish delay={0}>🐠</SwimmingFish>
            <SwimmingFish delay={5} sx={{ top: '60%', fontSize: '2.5rem' }}>
                🐟
            </SwimmingFish>
            <SwimmingFish delay={10} sx={{ top: '80%', fontSize: '4rem' }}>
                🦈
            </SwimmingFish>

            {/* Floating marine elements */}
            {marineElements.map((element, index) => (
                <FloatingElement
                    key={index}
                    delay={element.delay}
                    duration={element.duration}
                    sx={{
                        top: element.top,
                        left: element.left,
                    }}
                >
                    {element.emoji}
                </FloatingElement>
            ))}

            {/* Animated bubbles */}
            {bubbles.map((bubble) => (
                <Bubble
                    key={bubble.id}
                    delay={bubble.delay}
                    left={bubble.left}
                />
            ))}

            {/* Main content */}
            <Container maxWidth="md">
                <ContentBox>
                    <GlowText
                        variant="h1"
                        sx={{ fontSize: { xs: '4rem', md: '6rem' } }}
                    >
                        4🌊4
                    </GlowText>

                    <Typography
                        variant="h4"
                        sx={{
                            color: '#8cc63f',
                            marginBottom: '1rem',
                            fontSize: { xs: '1.8rem', md: '2.5rem' },
                        }}
                    >
                        Lost in the Deep Sea
                    </Typography>

                    <Typography
                        variant="body1"
                        sx={{
                            color: 'rgba(255, 255, 255, 0.8)',
                            marginBottom: '3rem',
                            fontSize: '1.2rem',
                            lineHeight: 1.6,
                        }}
                    >
                        The page you're looking for has drifted away into the
                        ocean depths. Don't worry, our marine friends will guide
                        you back to safety!
                    </Typography>

                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2,
                            justifyContent: 'center',
                            flexWrap: 'wrap',
                        }}
                    >
                        <StyledButton
                            variant="contained"
                            startIcon={<Home />}
                            onClick={handleGoHome}
                        >
                            Go to Home
                        </StyledButton>

                        <StyledButton
                            variant="outlined"
                            startIcon={<Waves />}
                            sx={{
                                borderColor: '#8cc63f',
                                color: '#8cc63f',
                                '&:hover': {
                                    borderColor: '#fff',
                                    backgroundColor: 'rgba(140, 198, 63, 0.1)',
                                },
                            }}
                            onClick={() => window.history.back()}
                        >
                            Go Back to Last Page
                        </StyledButton>
                    </Box>
                </ContentBox>
            </Container>
        </OceanContainer>
    )
}

export default ErrorPage
